import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  useTheme,
  alpha,
  Tabs,
  Tab,
  Button,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  Alert
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import GroupIcon from '@mui/icons-material/Group';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import api from '../../utils/api';

const CrewCalls = ({ data, onUpdate, projectId, styles }) => {
  const [loading, setLoading] = useState(false);
  const [projectCrew, setProjectCrew] = useState([]);
  const [error, setError] = useState(null);
  const [crewSectionExpanded, setCrewSectionExpanded] = useState(false);
  const theme = useTheme();
  // Toolbar filters
  const [crewSearch, setCrewSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('ALL');

  const fetchProjectCrew = useCallback(async () => {
    if (!projectId) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/crew/projects/${projectId}/crew`);
      setProjectCrew(response.data);
    } catch (err) {
      setError(err.message || 'Error fetching crew data');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProjectCrew();
  }, [fetchProjectCrew]);

  const saveCrewCalls = async (updatedData) => {
    try {
      // For new/unsaved callsheets, just update local state
      if (!data._id) {
        onUpdate({ ...data, crewCalls: updatedData.crewCalls });
        return;
      }
      
      setLoading(true);
      const response = await api.put(`/callsheets/${data._id}`, {
        crewCalls: updatedData.crewCalls
      });
      setError(null);
      return response.data;
    } catch (err) {
      let errorMsg = 'Failed to save crew calls';
      
      if (err.response) {
        errorMsg = err.response.data?.message || 
                  err.response.status === 400 ? 'Invalid crew data format' :
                  err.response.status === 404 ? 'Callsheet not found' :
                  err.response.status === 500 ? 'Server error' :
                  errorMsg;
      }
      
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleAddCrewCall = async () => {
    if (loading) return;
    setLoading(true);
    const currentCalls = Array.isArray(data.crewCalls) ? data.crewCalls : [];
    const newCrewCall = {
      name: '',
      department: '',
      role: '',
      callTime: '',
      notes: ''
    };
    const updatedCalls = [...currentCalls, newCrewCall];
    try {
      await saveCrewCalls({ ...data, crewCalls: updatedCalls });
      onUpdate({ ...data, crewCalls: updatedCalls });
    } catch {
      // Error already set by saveCrewCalls
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCrewCall = async (index) => {
    if (loading) return;
    setLoading(true);
    const currentCalls = Array.isArray(data.crewCalls) ? [...data.crewCalls] : [];
    currentCalls.splice(index, 1);
    
    try {
      await saveCrewCalls({ ...data, crewCalls: currentCalls });
      onUpdate({ ...data, crewCalls: currentCalls });
    } catch (err) {
      setError(err.message || 'Failed to delete crew call');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCrewCall = async (index, field, value) => {
    if (loading) return;
    const currentCalls = Array.isArray(data.crewCalls) ? [...data.crewCalls] : [];
    
    // Validate required fields
    if (field === 'name' && !value.trim()) {
      setError('Name is required');
      return;
    }
    if (field === 'department' && !value.trim()) {
      setError('Department is required');
      return;
    }
    if (field === 'callTime' && !isValidTime(value)) {
      setError('Please enter a valid time');
      return;
    }
    
    currentCalls[index] = { ...currentCalls[index], [field]: value };
    const updatedData = { ...data, crewCalls: currentCalls };
    
    try {
      await saveCrewCalls(updatedData);
      onUpdate(updatedData);
    } catch {
      // Error already set by saveCrewCalls
    }
  };

  const handleAddCrewMemberToCall = (crewMember) => {
    if (loading) return;
    const newCrewCall = {
      name: crewMember.user?.name || '',
      department: crewMember.department || '',
      role: crewMember.role || '',
      callTime: '',
      notes: ''
    };
    const currentCalls = Array.isArray(data.crewCalls) ? data.crewCalls : [];
    const updatedCalls = [...currentCalls, newCrewCall];
    onUpdate({ ...data, crewCalls: updatedCalls });
  };

  const toggleCrewSection = () => {
    setCrewSectionExpanded(!crewSectionExpanded);
  };

  // Group crew members by department
  const crewByDepartment = useMemo(() => {
    return projectCrew.reduce((acc, member) => {
      const department = member.department || 'Other';
      if (!acc[department]) {
        acc[department] = [];
      }
      // Ensure member has all required fields
      const processedMember = {
        ...member,
        name: member.user?.name || 'Unknown',
        department: department,
        role: member.role || 'Unknown'
      };
      acc[department].push(processedMember);
      return acc;
    }, {});
  }, [projectCrew]);

  const isValidTime = (time) => {
    if (!time) return false;
    // Accept HH:MM (24-hour) format
    const match = /^([0-1]\d|2[0-3]):([0-5]\d)$/.test(time);
    return match;
  };

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper 
          elevation={0}
          sx={{ 
            p: 4, 
            mb: 4,
            borderRadius: 1,
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            backgroundColor: theme.palette.background.paper,
            ...(styles?.primarySection || {})
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 4,
            pb: 3,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  background: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.primary.main, 0.15)
                    : alpha(theme.palette.primary.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <GroupIcon sx={{ color: 'primary.main', fontSize: 22 }} />
              </Box>
              <Box>
                <Typography 
                  variant="h4" 
                  component="h2" 
                  sx={{ 
                    fontWeight: 700,
                    letterSpacing: '-0.5px',
                    fontSize: '1.75rem',
                    color: theme.palette.text.primary,
                    mb: 0.5
                  }}
                >
                  Crew Calls
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    fontSize: '0.875rem'
                  }}
                >
                  Manage crew call times and assignments
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Chip 
                label={`${data.crewCalls?.length || 0} Crew`}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  '& .MuiChip-label': { px: 2 }
                }}
              />
              <Tooltip title="Add crew call row" arrow>
                <span>
                  <IconButton
                    onClick={handleAddCrewCall}
                    aria-label="Add crew call row"
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      color: theme.palette.primary.main,
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.12)
                      }
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              {projectId && projectCrew.length > 0 && (
                <Tooltip title={crewSectionExpanded ? 'Hide project crew' : 'Add from project crew'} arrow>
                  <span>
                    <IconButton
                      onClick={toggleCrewSection}
                      aria-label="Toggle project crew drawer"
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        color: theme.palette.primary.main,
                        backgroundColor: 'transparent',
                        transition: 'transform 0.2s ease',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.12)
                        }
                      }}
                    >
                      {crewSectionExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Search and Filter Toolbar */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search by name, role, or department..."
              value={crewSearch}
              onChange={(e) => setCrewSearch(e.target.value)}
              sx={{ 
                flex: 1,
                maxWidth: 400,
                '& .MuiInputLabel-root': {
                  fontSize: '0.95rem',
                  fontWeight: 600
                },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.default, 0.3)
                    : alpha(theme.palette.grey[50], 0.5),
                  borderRadius: 1,
                  transition: 'all 0.2s ease',
                  '& fieldset': {
                    borderWidth: 1.5
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.background.default, 0.4)
                      : alpha(theme.palette.grey[50], 0.8),
                    '& fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  },
                  '&.Mui-focused': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.primary.main, 0.05)
                      : alpha(theme.palette.primary.main, 0.02),
                    '& fieldset': {
                      borderWidth: 2
                    }
                  }
                }
              }}
            />
            <TextField
              size="small"
              select
              label="Department"
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
              sx={{ 
                width: 220,
                '& .MuiInputLabel-root': {
                  fontSize: '0.95rem',
                  fontWeight: 600
                },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.default, 0.3)
                    : alpha(theme.palette.grey[50], 0.5),
                  borderRadius: 1,
                  transition: 'all 0.2s ease',
                  '& fieldset': {
                    borderWidth: 1.5
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.background.default, 0.4)
                      : alpha(theme.palette.grey[50], 0.8),
                    '& fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  },
                  '&.Mui-focused': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.primary.main, 0.05)
                      : alpha(theme.palette.primary.main, 0.02),
                    '& fieldset': {
                      borderWidth: 2
                    }
                  }
                }
              }}
              SelectProps={{ native: false }}
            >
              <MenuItem value="ALL">All Departments</MenuItem>
              {Array.from(new Set((data.crewCalls||[]).map(c => c.department).filter(Boolean))).map(dep => (
                <MenuItem key={dep} value={dep}>{dep}</MenuItem>
              ))}
            </TextField>
          </Box>

          <TableContainer 
            component={Paper} 
            variant="outlined"
            sx={{ 
              mt: 2,
              mb: 3,
              borderRadius: 1,
              overflow: 'hidden',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 8px 32px rgba(0,0,0,0.4)'
                : '0 4px 16px rgba(0,0,0,0.08)',
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(20, 20, 25, 0.8)'
                : theme.palette.background.paper,
              maxHeight: '60vh',
              ...(styles?.table || {}),
              '& .MuiTableCell-head': {
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(30, 30, 40, 0.8)' 
                  : 'rgba(240, 240, 250, 0.8)',
                color: theme.palette.mode === 'dark' 
                  ? theme.palette.primary.light 
                  : theme.palette.primary.dark,
                fontWeight: 'bold'
              },
              '& .MuiTableRow-root:nth-of-type(even)': {
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(40, 40, 50, 0.3)' 
                  : 'rgba(245, 245, 255, 0.3)'
              },
              '& .MuiTableCell-root': {
                borderBottomColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.1)' 
                  : 'rgba(0, 0, 0, 0.1)',
                color: theme.palette.text.primary
              }
            }}
          >
            <Table 
              sx={{ 
                minWidth: 650,
                '& .MuiTableCell-root': {
                  borderColor: theme.palette.mode === 'dark' 
                    ? alpha(theme.palette.divider, 0.1)
                    : theme.palette.divider
                }
              }}
              stickyHeader
              size="small"
            >
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      backgroundColor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.black, 0.3)
                        : alpha(theme.palette.primary.main, 0.05),
                      width: '20%'
                    }}
                  >
                    Name
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      backgroundColor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.black, 0.3)
                        : alpha(theme.palette.primary.main, 0.05),
                      width: '15%'
                    }}
                  >
                    Department
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      backgroundColor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.black, 0.3)
                        : alpha(theme.palette.primary.main, 0.05),
                      width: '15%'
                    }}
                  >
                    Role
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      backgroundColor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.black, 0.3)
                        : alpha(theme.palette.primary.main, 0.05),
                      width: '15%'
                    }}
                  >
                    Call Time
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      backgroundColor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.black, 0.3)
                        : alpha(theme.palette.primary.main, 0.05),
                      width: '25%'
                    }}
                  >
                    Notes
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      backgroundColor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.black, 0.3)
                        : alpha(theme.palette.primary.main, 0.05),
                      width: '10%'
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(Array.isArray(data.crewCalls) ? data.crewCalls : [])
                  .filter(call => {
                    if (!crewSearch) return true;
                    const s = crewSearch.toLowerCase();
                    return (
                      (call.name && call.name.toLowerCase().includes(s)) ||
                      (call.role && call.role.toLowerCase().includes(s)) ||
                      (call.department && call.department.toLowerCase().includes(s)) ||
                      (call.notes && call.notes.toLowerCase().includes(s))
                    );
                  })
                  .filter(call => deptFilter === 'ALL' ? true : (call.department === deptFilter))
                  .map((call, index) => (
                  <TableRow 
                    key={index}
                    sx={{
                      '&:hover': {
                        bgcolor: theme.palette.mode === 'dark'
                          ? alpha(theme.palette.primary.main, 0.1)
                          : alpha(theme.palette.primary.light, 0.1)
                      },
                      '& .MuiTableCell-root': {
                        py: 1
                      }
                    }}
                  >
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={call.name || ''}
                        onChange={(e) => handleUpdateCrewCall(index, 'name', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.mode === 'dark'
                              ? alpha(theme.palette.background.default, 0.3)
                              : alpha(theme.palette.grey[50], 0.5),
                            borderRadius: 1,
                            '& fieldset': {
                              borderWidth: 1
                            },
                            '&:hover fieldset': {
                              borderColor: theme.palette.primary.main
                            },
                            '&.Mui-focused': {
                              backgroundColor: theme.palette.mode === 'dark'
                                ? alpha(theme.palette.primary.main, 0.05)
                                : alpha(theme.palette.primary.main, 0.02),
                              '& fieldset': {
                                borderWidth: 1.5
                              }
                            }
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={call.department || ''}
                        onChange={(e) => handleUpdateCrewCall(index, 'department', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.mode === 'dark'
                              ? alpha(theme.palette.background.default, 0.3)
                              : alpha(theme.palette.grey[50], 0.5),
                            borderRadius: 1,
                            '& fieldset': {
                              borderWidth: 1
                            },
                            '&:hover fieldset': {
                              borderColor: theme.palette.primary.main
                            },
                            '&.Mui-focused': {
                              backgroundColor: theme.palette.mode === 'dark'
                                ? alpha(theme.palette.primary.main, 0.05)
                                : alpha(theme.palette.primary.main, 0.02),
                              '& fieldset': {
                                borderWidth: 1.5
                              }
                            }
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={call.role || ''}
                        onChange={(e) => handleUpdateCrewCall(index, 'role', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.mode === 'dark'
                              ? alpha(theme.palette.background.default, 0.3)
                              : alpha(theme.palette.grey[50], 0.5),
                            borderRadius: 1,
                            '& fieldset': {
                              borderWidth: 1
                            },
                            '&:hover fieldset': {
                              borderColor: theme.palette.primary.main
                            },
                            '&.Mui-focused': {
                              backgroundColor: theme.palette.mode === 'dark'
                                ? alpha(theme.palette.primary.main, 0.05)
                                : alpha(theme.palette.primary.main, 0.02),
                              '& fieldset': {
                                borderWidth: 1.5
                              }
                            }
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                        <TimePicker
                          ampm
                          views={['hours', 'minutes']}
                          value={call.callTime ? new Date(`2000-01-01T${call.callTime}`) : null}
                          onChange={(newValue) => {
                            if (!newValue) {
                              handleUpdateCrewCall(index, 'callTime', '');
                              return;
                            }
                            try {
                              let hours, minutes;
                              // Support Dayjs or Date
                              if (typeof newValue?.getHours === 'function') {
                                hours = newValue.getHours();
                                minutes = newValue.getMinutes();
                              } else if (typeof newValue?.hour === 'function') {
                                hours = newValue.hour();
                                minutes = newValue.minute();
                              } else if (newValue?.$d instanceof Date) {
                                hours = newValue.$d.getHours();
                                minutes = newValue.$d.getMinutes();
                              } else if (typeof newValue?.toDate === 'function') {
                                const d = newValue.toDate();
                                hours = d.getHours();
                                minutes = d.getMinutes();
                              }
                              if (typeof hours === 'number' && typeof minutes === 'number') {
                                const h = String(hours).padStart(2, '0');
                                const m = String(minutes).padStart(2, '0');
                                handleUpdateCrewCall(index, 'callTime', `${h}:${m}`);
                              }
                            } catch (e) {
                              // no-op; avoid crashing UI
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Call Time"
                              size="small"
                              sx={{
                                '& .MuiInputBase-input': { textAlign: 'center' },
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.background.default, 0.3)
                                    : alpha(theme.palette.grey[50], 0.5),
                                  borderRadius: 1,
                                  '& fieldset': {
                                    borderWidth: 1
                                  },
                                  '&:hover fieldset': {
                                    borderColor: theme.palette.primary.main
                                  },
                                  '&.Mui-focused': {
                                    backgroundColor: theme.palette.mode === 'dark'
                                      ? alpha(theme.palette.primary.main, 0.05)
                                      : alpha(theme.palette.primary.main, 0.02),
                                    '& fieldset': {
                                      borderWidth: 1.5
                                    }
                                  }
                                }
                              }}
                            />
                          )}
                        />
                    </TableCell>
                    <TableCell sx={{ minWidth: 280 }}>
                      <TextField
                        fullWidth
                        placeholder="Notes"
                        size="small"
                        value={call.notes || ''}
                        onChange={(e) => handleUpdateCrewCall(index, 'notes', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.mode === 'dark'
                              ? alpha(theme.palette.background.default, 0.3)
                              : alpha(theme.palette.grey[50], 0.5),
                            borderRadius: 1,
                            '& fieldset': {
                              borderWidth: 1
                            },
                            '&:hover fieldset': {
                              borderColor: theme.palette.primary.main
                            },
                            '&.Mui-focused': {
                              backgroundColor: theme.palette.mode === 'dark'
                                ? alpha(theme.palette.primary.main, 0.05)
                                : alpha(theme.palette.primary.main, 0.02),
                              '& fieldset': {
                                borderWidth: 1.5
                              }
                            }
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleDeleteCrewCall(index)}
                        sx={{
                          color: theme.palette.error.main,
                          '&:hover': {
                            bgcolor: alpha(theme.palette.error.main, 0.1)
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {projectId && projectCrew.length > 0 && (
            <Box sx={{ mt: 2 }}>
              {crewSectionExpanded && (
                <Box sx={{ 
                  mt: 2,
                  p: 3,
                  borderRadius: 1,
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(30, 30, 35, 0.4)' 
                    : 'rgba(255, 255, 255, 0.8)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 4px 20px rgba(0,0,0,0.4)'
                    : '0 2px 12px rgba(0,0,0,0.1)'
                }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      mb: 2, 
                      color: theme.palette.mode === 'dark' 
                        ? theme.palette.text.secondary 
                        : theme.palette.text.primary,
                      fontWeight: 500
                    }}
                  >
                    Click to add crew member to call sheet:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {Object.entries(crewByDepartment).map(([department, members]) => (
                      <Box key={department} sx={{ mb: 3, width: '100%' }}>
                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            mb: 1.5, 
                            fontWeight: 600,
                            color: theme.palette.mode === 'dark'
                              ? theme.palette.primary.light
                              : theme.palette.primary.dark,
                            textShadow: theme.palette.mode === 'dark'
                              ? '0 2px 4px rgba(0,0,0,0.4)'
                              : '0 1px 2px rgba(0,0,0,0.1)'
                          }}
                        >
                          {department}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {members.map((crew, index) => (
                            <Chip
                              key={index}
                              label={`${crew.name} (${crew.role})`}
                              onClick={() => handleAddCrewMemberToCall(crew)}
                              sx={{
                                borderRadius: '8px',
                                height: '32px',
                                fontSize: '0.85rem',
                                fontWeight: 500,
                                backgroundColor: theme.palette.mode === 'dark' 
                                  ? alpha(theme.palette.primary.main, 0.15)
                                  : alpha(theme.palette.primary.light, 0.15),
                                color: theme.palette.mode === 'dark'
                                  ? theme.palette.primary.light
                                  : theme.palette.primary.dark,
                                border: `1px solid ${theme.palette.mode === 'dark'
                                  ? alpha(theme.palette.primary.main, 0.2)
                                  : alpha(theme.palette.primary.main, 0.3)}`,
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                  backgroundColor: theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.primary.main, 0.25)
                                    : alpha(theme.palette.primary.light, 0.25),
                                  transform: 'translateY(-1px)',
                                  boxShadow: theme.palette.mode === 'dark'
                                    ? '0 4px 8px rgba(0,0,0,0.4)'
                                    : '0 2px 4px rgba(0,0,0,0.2)'
                                },
                                '&:active': {
                                  transform: 'translateY(0)',
                                  boxShadow: 'none'
                                }
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

CrewCalls.propTypes = {
  data: PropTypes.shape({
    crewCalls: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        department: PropTypes.string,
        role: PropTypes.string,
        callTime: PropTypes.string,
        notes: PropTypes.string
      })
    )
  }),
  onUpdate: PropTypes.func.isRequired,
  projectId: PropTypes.string,
  styles: PropTypes.object
};

export default memo(CrewCalls);
